/**
 * Negation-aware sentiment classification for free-text feedback.
 *
 * This is the one part of feedback intelligence that genuinely cannot be a pack
 * metric. It tokenizes, then flips polarity when a negator precedes a sentiment
 * term within a window — SQL has no tokenizer and no positional lookbehind, so
 * a `LIKE '%good%'` translation would score "not very good" as positive.
 *
 * Everything else that used to live here — volume, sentiment distribution, net
 * sentiment, rating averages, period-over-period trend — moved to SQL in
 * `domainPacks/customerFeedback.ts`, where Explore can chart and alert on it.
 *
 * This reference classifier is not part of connector ingestion. Domain packs count
 * a source-provided `sentiment` column when present and omit sentiment metrics
 * when it is absent.
 */
export var FeedbackSentiment;
(function (FeedbackSentiment) {
    FeedbackSentiment["POSITIVE"] = "positive";
    FeedbackSentiment["NEUTRAL"] = "neutral";
    FeedbackSentiment["NEGATIVE"] = "negative";
})(FeedbackSentiment || (FeedbackSentiment = {}));
export const FEEDBACK_POSITIVE_TERMS = [
    'amazing',
    'awesome',
    'brilliant',
    'clear',
    'easy',
    'excellent',
    'fast',
    'good',
    'great',
    'happy',
    'helpful',
    'intuitive',
    'love',
    'loved',
    'perfect',
    'recommend',
    'reliable',
    'smooth',
    'useful',
];
export const FEEDBACK_NEGATIVE_TERMS = [
    'annoying',
    'awful',
    'bad',
    'broken',
    'buggy',
    'confusing',
    'crash',
    'crashes',
    'difficult',
    'disappointing',
    'expensive',
    'fails',
    'frustrating',
    'hate',
    'hated',
    'poor',
    'slow',
    'terrible',
    'unreliable',
    'useless',
    'worst',
];
/** Any token ending in "n't" also counts as a negator. */
export const FEEDBACK_NEGATORS = [
    'barely',
    'cannot',
    'hardly',
    'neither',
    'never',
    'no',
    'nor',
    'not',
    'nothing',
    'without',
];
/** How many preceding tokens a negator flips. "not very good" -> negative. */
const NEGATION_WINDOW = 3;
const tokenize = (text) => text
    .toLowerCase()
    .split(/[^a-z0-9']+/)
    .filter((token) => token.length > 0);
const isNegator = (token) => token.endsWith("n't") ||
    FEEDBACK_NEGATORS.includes(token);
/**
 * ponytail: deterministic classifier, no LLM in the core — it is unit-testable,
 * free and offline. Swap this one function for an LLM/model call later; callers
 * see the same `FeedbackSentiment`, so nothing downstream changes.
 */
export const classifyText = (text) => {
    const tokens = tokenize(text);
    let score = 0;
    tokens.forEach((token, index) => {
        let polarity = 0;
        if (FEEDBACK_POSITIVE_TERMS.includes(token)) {
            polarity = 1;
        }
        else if (FEEDBACK_NEGATIVE_TERMS.includes(token)) {
            polarity = -1;
        }
        if (polarity === 0)
            return;
        const negated = tokens
            .slice(Math.max(0, index - NEGATION_WINDOW), index)
            .some(isNegator);
        score += negated ? -polarity : polarity;
    });
    if (score > 0)
        return FeedbackSentiment.POSITIVE;
    if (score < 0)
        return FeedbackSentiment.NEGATIVE;
    return FeedbackSentiment.NEUTRAL;
};
//# sourceMappingURL=sentimentClassifier.js.map